/* eslint-disable react/prop-types */
import _isEmpty from 'lodash/isEmpty';

import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Form } from 'react-bulma-components';
import Select, { components } from 'react-select';

import checkIcon from 'assets/images/v2/check-icon.svg';
import chevronDownIcon from 'assets/images/v2/chevron-down-icon.svg';
import closeIcon from 'assets/images/v2/close-icon.svg';

import CustomCheckboxV2 from '../checkbox';

import './select.scss';

const { Field, Label } = Form;

const MAX_VISIBLE_ITEMS = 3;

const CustomSelectV2 = (props) => {
    const {
        value,
        className,
        label,
        name,
        onChange,
        errors,
        required,
        id,
        options = [],
        multiple = false,
        placeholder
    } = props;

    const [selectedValues, setSelectedValues] = useState([]);

    const hasErrors = !_isEmpty(errors);

    const customOnChange = (isMultiple, values) => {
        if (!isMultiple) {
            return;
        }
        setSelectedValues(values);
    };

    const handleRemoveItem = (selectProps, data) => {
        setSelectedValues((prev) => prev.filter((item) => item.id !== data.id));
        const { name } = selectProps;
        onChange({ target: { name, value: selectedValues.filter((item) => item.id !== data.id) } });
    };

    const DropdownIndicator = (props) => {
        const { selectProps } = props;
        const isMenuOpen = selectProps.menuIsOpen;

        return (
            <components.DropdownIndicator {...props}>
                <img
                    src={chevronDownIcon}
                    width={16}
                    height={16}
                    style={{
                        paddingTop: isMenuOpen ? '0px' : '2px',
                        transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}
                />
            </components.DropdownIndicator>
        );
    };

    const ClearIndicator = (props) => {
        return (
            <components.ClearIndicator {...props}>
                <img
                    src={closeIcon}
                    width={14}
                    height={14}
                    style={{
                        paddingTop: '2px'
                    }}
                />
            </components.ClearIndicator>
        );
    };

    const SingleSelectOption = (props) => {
        const { data, isSelected } = props;

        return (
            <components.Option {...props}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span>{data.name}</span>
                    {isSelected && (
                        <img
                            src={checkIcon}
                            width={16}
                            height={16}
                            alt='Selected'
                            className='custom-select__check-icon'
                        />
                    )}
                </div>
            </components.Option>
        );
    };

    const MultiSelectOption = (props) => {
        const { data, isSelected, selectProps } = props;
        const isMultiSelect = selectProps.isMulti;

        if (!isMultiSelect) {
            return <components.Option {...props} />;
        }

        return (
            <components.Option {...props} className='custom-select__option-multi'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CustomCheckboxV2
                        name={`option-${data.value || data.name}`}
                        checked={isSelected}
                        onChange={() => {}} // handled by react-select
                    />
                    <span>{data.name}</span>
                </div>
            </components.Option>
        );
    };

    const MultiValueContainer = ({ selectProps, data }) => {
        const { value } = selectProps;
        const selectedValues = value;
        if (selectedValues.length > MAX_VISIBLE_ITEMS) {
            if (selectedValues.indexOf(data) === MAX_VISIBLE_ITEMS) {
                return (
                    <div className='custom-select__multiple--more'>
                        <span>
                            {selectedValues.length > MAX_VISIBLE_ITEMS &&
                                `${selectedValues.length - MAX_VISIBLE_ITEMS}`}
                        </span>
                    </div>
                );
            } else if (selectedValues.indexOf(data) >= MAX_VISIBLE_ITEMS) {
                return null;
            }
        }
        return (
            <components.MultiValueContainer {...selectProps}>
                <div className='custom-select__multiple--selected'>
                    {data.name}
                    <img
                        src={closeIcon}
                        width={12}
                        height={12}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleRemoveItem(selectProps, data);
                        }}
                    />
                </div>
            </components.MultiValueContainer>
        );
    };

    useEffect(() => {
        setSelectedValues(value);
    }, []);

    return (
        <Field className='custom-select__container'>
            {label && (
                <Label id={id || name} className='custom-select__label'>
                    <span>
                        {label}
                        {required && <span className='required-mark'>*</span>}
                    </span>
                </Label>
            )}
            {multiple ? (
                <Select
                    isClearable
                    name={name}
                    options={options}
                    value={selectedValues}
                    classNamePrefix='custom-select'
                    isMulti={multiple}
                    placeholder={placeholder}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.name}
                    onChange={(value) => {
                        customOnChange(multiple, value);
                        onChange({ target: { name, value } });
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        indicatorSeparator: () => ({
                            display: 'none'
                        })
                    }}
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    components={{ MultiValueContainer, DropdownIndicator, ClearIndicator, Option: MultiSelectOption }}
                    className={className}
                />
            ) : (
                <Select
                    isClearable
                    name={name}
                    options={options}
                    value={value}
                    classNamePrefix='custom-select'
                    placeholder={placeholder}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.name}
                    onChange={(value) => {
                        onChange({ target: { name, value } });
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        indicatorSeparator: () => ({
                            display: 'none'
                        })
                    }}
                    components={{ DropdownIndicator, ClearIndicator, Option: SingleSelectOption }}
                />
            )}
            {hasErrors && (
                <div className='custom-input__errors'>
                    {errors.map((error) => (
                        <p key={error}>{error}</p>
                    ))}
                </div>
            )}
        </Field>
    );
};

export default CustomSelectV2;

CustomSelectV2.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.arrayOf(PropTypes.object)
    ]),
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    errors: PropTypes.arrayOf(PropTypes.string),
    required: PropTypes.bool,
    id: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object),
    multiple: PropTypes.bool,
    className: PropTypes.string,
    selectProps: PropTypes.object,
    placeholder: PropTypes.string
};
